import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useData } from '../context/DataContext';

export const ServiceContactModal = ({ isOpen, onClose, serviceName }) => {
  const { submitContact } = useData();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await submitContact({
        name: formData.name,
        email: formData.email,
        phone: null,
        company: formData.company || null,
        message: `Interesado en: ${serviceName}`,
        subject: `Consulta sobre ${serviceName} - ${formData.company || 'Cliente Potencial'}`,
        status: 'pending'
      });

      if (result.success) {
        setSubmitted(true);
        toast.success('Recibimos tu consulta. Te contactaremos pronto.');
        setTimeout(() => {
          onClose();
          setSubmitted(false);
          setFormData({ name: '', email: '', company: '' });
        }, 2000);
      } else {
        toast.error('Error al enviar. Por favor, intenta de nuevo.');
      }
    } catch (error) {
      toast.error('Error al enviar. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-card border-border rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold text-foreground">
            Consultar sobre {serviceName}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Completa tus datos y te contactaremos en menos de 2 horas.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-10 text-center space-y-4">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-8 w-8 text-accent" />
            </div>
            <p className="text-lg font-semibold text-foreground">Consulta enviada</p>
            <p className="text-muted-foreground">Te contactaremos pronto.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <label htmlFor="modal-name" className="text-sm font-medium text-foreground">
                Nombre *
              </label>
              <Input
                id="modal-name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleInputChange}
                className="border-border bg-background focus:border-accent focus:ring-accent rounded-xl h-12"
                placeholder="Tu nombre"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="modal-email" className="text-sm font-medium text-foreground">
                Email *
              </label>
              <Input
                id="modal-email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="border-border bg-background focus:border-accent focus:ring-accent rounded-xl h-12"
                placeholder="tu@email.com"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="modal-company" className="text-sm font-medium text-foreground">
                Empresa *
              </label>
              <Input
                id="modal-company"
                name="company"
                type="text"
                required
                value={formData.company}
                onChange={handleInputChange}
                className="border-border bg-background focus:border-accent focus:ring-accent rounded-xl h-12"
                placeholder="Nombre de tu empresa"
              />
            </div>

            <div className="bg-accent/10 border border-accent/20 rounded-xl p-4 text-sm text-foreground">
              Servicio seleccionado: <strong>{serviceName}</strong>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-foreground text-background hover:bg-foreground/90 rounded-full py-5 font-medium transition-all duration-300 group disabled:opacity-50"
            >
              {loading ? 'Enviando...' : 'Enviar Consulta'}
              <Send className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
